import { useEffect, useRef, useState } from "preact/hooks";
import type { SavedSearch } from "../../shared/types";
import { api } from "../api";
import { mailHash, navigate } from "../router";
import { buildQuery, parseQuery, type SearchFields } from "../search-query";
import { app, errorToast, refreshSearches, toast, useStore } from "../store";
import { Toggle } from "./ui";

/** The search box's dropdown: fields for each operator, plus saving the search to the sidebar. */
export const AdvancedSearch = ({ query, onClose }: { query: string; onClose: () => void }) => {
  const folders = useStore(app, (state) => state.folders);
  const [fields, setFields] = useState<SearchFields>(() => parseQuery(query));
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panel.current?.querySelector("input")?.focus();
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        panel.current &&
        !panel.current.contains(target) &&
        !(target as Element).closest?.(".search-advanced")
      )
        onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const text = (key: keyof SearchFields, label: string, placeholder = "", type = "text") => (
    <label class="adv-field">
      <span>{label}</span>
      <input
        type={type}
        placeholder={placeholder}
        value={fields[key] as string}
        onInput={(event) =>
          setFields({ ...fields, [key]: (event.target as HTMLInputElement).value })
        }
      />
    </label>
  );
  const built = buildQuery(fields);
  const search = () => {
    if (!built) return;
    navigate(mailHash(`#/search/${encodeURIComponent(built)}`));
    onClose();
  };

  return (
    // A div, not a form: it sits inside the search box's form.
    <div
      ref={panel}
      class="advanced-search"
      role="dialog"
      aria-label="Advanced search"
      onKeyDown={(event) => {
        if (event.key === "Enter" && (event.target as HTMLElement).tagName === "INPUT") {
          event.preventDefault();
          event.stopPropagation();
          search();
        }
      }}
    >
      {text("from", "From", "name or address")}
      {text("to", "To", "name or address")}
      {text("subject", "Subject")}
      {text("words", "Has the words")}
      {text("without", "Doesn't have")}
      <label class="adv-field">
        <span>Search in</span>
        <select
          value={fields.folder}
          onChange={(event) =>
            setFields({ ...fields, folder: (event.target as HTMLSelectElement).value })
          }
        >
          <option value="">All mail</option>
          {folders.map((folder) => (
            <option key={folder.id} value={folder.role ?? folder.name}>
              {folder.name}
            </option>
          ))}
        </select>
      </label>
      <div class="adv-field">
        <span>Date</span>
        <div class="adv-range">
          <input
            type="date"
            aria-label="After"
            value={fields.after}
            onInput={(event) =>
              setFields({ ...fields, after: (event.target as HTMLInputElement).value })
            }
          />
          <span class="muted">to</span>
          <input
            type="date"
            aria-label="Before"
            value={fields.before}
            onInput={(event) =>
              setFields({ ...fields, before: (event.target as HTMLInputElement).value })
            }
          />
        </div>
      </div>
      {text("larger", "Larger than", "e.g. 5M or 200k")}
      <div class="adv-toggles">
        <Toggle
          label="Has attachment"
          checked={fields.hasAttachment}
          onChange={(hasAttachment) => setFields({ ...fields, hasAttachment })}
        />
        <Toggle
          label="Unread"
          checked={fields.unread}
          onChange={(unread) => setFields({ ...fields, unread })}
        />
        <Toggle
          label="Flagged"
          checked={fields.flagged}
          onChange={(flagged) => setFields({ ...fields, flagged })}
        />
      </div>
      {built && <code class="adv-preview">{built}</code>}
      <div class="adv-actions">
        <button
          type="button"
          class="ghost"
          disabled={!built}
          onClick={() => void saveSearch(built).then((saved) => saved && onClose())}
        >
          Save search
        </button>
        <button type="button" class="primary" disabled={!built} onClick={search}>
          Search
        </button>
      </div>
    </div>
  );
};

/** Asks for a name and keeps the search in the sidebar. */
export const saveSearch = async (query: string) => {
  const name = window.prompt("Name this search", query.slice(0, 60));
  if (!name?.trim()) return null;
  try {
    const saved = await api.post<SavedSearch>("/searches", { name: name.trim(), query });
    await refreshSearches();
    toast(`Saved "${saved.name}" to the sidebar`);
    return saved;
  } catch (error) {
    errorToast(error);
    return null;
  }
};
